import { Collection, fromJS, Map } from 'immutable'
import {
  GET_BILLING_ORDER_PRICE_DATA,
  GET_DELIVERY_SERVICES_OPTIMUS,
  GET_DEPARTMENT_SERVICES_OPTIMUS,
  GET_INVOICED_ORDER_PRICE_DATA,
  GET_LATEST_PRICE_UPDATE,
  GET_MULTILEG_ORDER_PRICE_DATA,
  GET_MULTISTOP_ORDER_PRICE_DATA,
  GET_ORDER_PRICE_DATA,
  GET_ORDER_PRICE_DATA_RESET,
  GET_PICKUP_SERVICES_OPTIMUS,
  GET_SERVICES_OPTIMUS,
  RESET_ORDER_PRICE_INFO,
  ROUTER_LOCATION_CHANGE_ACTION,
  SET_PRICE_EXPLANATION_LIST
} from '../actions/actionTypes'
import { AnyData } from '../actions/creators/baseHelpers'
import { ImmutableMap } from '../types/immutableTypes'

const initialState: ServiceStateImmutable = Map({
  pickupServices: Map(),
  deliveryServices: Map(),
  departmentServices: Map(),
  price: Map(),
  latestPriceUpdate: null,
  errors: [],
  isLoadingPrice: false,
  isLoadingServices: false,
  useRecipientRouting: false
} as ServiceState)

export interface ServiceState {
  pickupServices: Collection<unknown, unknown>
  deliveryServices: Collection<unknown, unknown>
  departmentServices: Collection<unknown, unknown>
  price: Collection<unknown, unknown>
  latestPriceUpdate: Map<unknown, unknown> | null
  errors: unknown
  isLoadingPrice: boolean
  isLoadingServices: boolean
  useRecipientRouting: boolean
}

interface ActionProp {
  type: string
  body: AnyData
  payload: { isFirstRendering: boolean }
  explanations?: Array<Record<string, unknown>>
}

interface ServiceStateImmutable extends ImmutableMap<ServiceState> {}

export default function reducer(state = initialState, action: ActionProp) {
  switch (action.type) {
    case GET_PICKUP_SERVICES_OPTIMUS.SUCCESS:
      return state.withMutations((s) =>
        s
          .set('pickupServices', fromJS(action.body.pickupServices))
          .set('errors', fromJS(action.body.errors))
          .set('isLoadingServices', false)
          .set('useRecipientRouting', fromJS(action.body.useRecipientRouting))
      )
    case GET_DELIVERY_SERVICES_OPTIMUS.SUCCESS:
      return state.withMutations((s) =>
        s
          .set('deliveryServices', fromJS(action.body.deliveryServices))
          .set('errors', fromJS(action.body.errors))
          .set('isLoadingServices', false)
          .set('useRecipientRouting', fromJS(action.body.useRecipientRouting))
      )
    case GET_SERVICES_OPTIMUS.SUCCESS:
      return state.withMutations((s) =>
        s
          .set('pickupServices', fromJS(action.body.pickupServices))
          .set('deliveryServices', fromJS(action.body.deliveryServices))
          .set('errors', fromJS(action.body.errors))
          .set('isLoadingServices', false)
          .set('useRecipientRouting', fromJS(action.body.useRecipientRouting))
      )
    case GET_DEPARTMENT_SERVICES_OPTIMUS.SUCCESS:
      return state.withMutations((s) =>
        s
          .set('departmentServices', fromJS(action.body.departmentServices))
          .set('errors', fromJS(action.body.errors))
          .set('isLoadingServices', false)
      )
    case GET_SERVICES_OPTIMUS.REQUEST:
    case GET_PICKUP_SERVICES_OPTIMUS.REQUEST:
    case GET_DELIVERY_SERVICES_OPTIMUS.REQUEST:
    case GET_DEPARTMENT_SERVICES_OPTIMUS.REQUEST:
      return state.set('isLoadingServices', true)
    case GET_ORDER_PRICE_DATA.SUCCESS:
      return state.set('price', fromJS(action.body)).set('isLoadingPrice', false)
    case GET_ORDER_PRICE_DATA.REQUEST:
      return state.set('isLoadingPrice', true)
    case GET_ORDER_PRICE_DATA_RESET:
      return state.set('price', fromJS({}))
    case GET_MULTISTOP_ORDER_PRICE_DATA.SUCCESS:
      return state.set('price', fromJS(action.body)).set('isLoadingPrice', false)
    case GET_MULTISTOP_ORDER_PRICE_DATA.REQUEST:
      return state.set('isLoadingPrice', true)
    case GET_MULTISTOP_ORDER_PRICE_DATA.FAILURE:
    case GET_MULTILEG_ORDER_PRICE_DATA.SUCCESS:
      return state.set('price', fromJS(action.body)).set('isLoadingPrice', false)
    case GET_MULTILEG_ORDER_PRICE_DATA.REQUEST:
      return state.set('isLoadingPrice', true)
    case GET_MULTILEG_ORDER_PRICE_DATA.FAILURE:
    case GET_INVOICED_ORDER_PRICE_DATA.SUCCESS:
      return state.set('price', fromJS(action.body)).set('isLoadingPrice', false)
    case GET_INVOICED_ORDER_PRICE_DATA.REQUEST:
      return state.set('isLoadingPrice', true)
    case GET_BILLING_ORDER_PRICE_DATA.REQUEST:
      return state.set('isLoadingPrice', true)
    case GET_BILLING_ORDER_PRICE_DATA.SUCCESS:
      return state.set('latestPriceUpdate', fromJS({ ...action.body, hasPrice: true })).set('isLoadingPrice', false)
    case GET_BILLING_ORDER_PRICE_DATA.FAILURE:
      return state.set('isLoadingPrice', false)
    case SET_PRICE_EXPLANATION_LIST:
      return state.update('latestPriceUpdate', (latestPriceUpdate) =>
        Map.isMap(latestPriceUpdate)
          ? latestPriceUpdate.set('explanations', fromJS(action.explanations ?? []))
          : Map({ explanations: fromJS(action.explanations ?? []) })
      )
    case GET_LATEST_PRICE_UPDATE.SUCCESS: {
      const explanations = action.body?.explanations
      const hasExplanations = Array.isArray(explanations) && explanations.length > 0
      const body = hasExplanations ? { ...action.body, hasPrice: true } : action.body
      return state.set('latestPriceUpdate', fromJS(body))
    }
    case GET_LATEST_PRICE_UPDATE.REQUEST:
    case GET_LATEST_PRICE_UPDATE.FAILURE:
    case GET_INVOICED_ORDER_PRICE_DATA.FAILURE:
    case GET_ORDER_PRICE_DATA.FAILURE:
    case GET_MULTILEG_ORDER_PRICE_DATA.FAILURE:
    case GET_MULTISTOP_ORDER_PRICE_DATA.FAILURE:
    case GET_INVOICED_ORDER_PRICE_DATA.FAILURE:
    case GET_SERVICES_OPTIMUS.FAILURE:
    case GET_PICKUP_SERVICES_OPTIMUS.FAILURE:
    case GET_DELIVERY_SERVICES_OPTIMUS.FAILURE:
    case GET_DEPARTMENT_SERVICES_OPTIMUS.FAILURE:
      return state.set('isLoadingPrice', false).set('isLoadingServices', false)
    case ROUTER_LOCATION_CHANGE_ACTION:
      return action.payload?.isFirstRendering ? initialState : state
    case RESET_ORDER_PRICE_INFO:
      return state.withMutations((s) => s.set('price', Map()))
    default:
      return state
  }
}
